import { useReveal } from '@/hooks/useReveal';

const offices = [
  {
    id: 'bengaluru',
    icon: 'ri-building-2-line',
    badge: 'Head Office',
    title: 'Bengaluru',
    address: [
      'Unit 101,',
      'Oxford Towers,',
      '139/88, HAL Old Airport Rd,',
      'Kodihalli,',
      'Bengaluru,',
      'Karnataka – 560008',
    ],
    buttons: [
      {
        label: 'View on Google Maps',
        icon: 'ri-map-pin-line',
        href: 'https://maps.google.com/?q=Oxford+Towers,+HAL+Old+Airport+Rd,+Kodihalli,+Bengaluru,+Karnataka+560008',
        external: true,
        primary: true,
      },
      {
        label: 'Call Office',
        icon: 'ri-phone-line',
        href: 'tel:+919746765488',
        external: false,
        primary: false,
      },
    ],
    accentColor: 'primary',
  },
  {
    id: 'kerala',
    icon: 'ri-map-pin-2-line',
    badge: 'Branch Office',
    title: 'Kerala',
    address: [
      'Digi Smart,',
      'Stadium Road,',
      'Opp. Stadium,',
      'Kuthuparamba,',
      'Kannur,',
      'Kerala – 670643',
    ],
    buttons: [
      {
        label: 'View on Google Maps',
        icon: 'ri-map-pin-line',
        href: 'https://maps.google.com/?q=Digi+Smart,+Stadium+Road,+Kuthuparamba,+Kannur,+Kerala+670643',
        external: true,
        primary: true,
      },
      {
        label: 'WhatsApp Us',
        icon: 'ri-whatsapp-line',
        href: 'https://wa.me/919746765488?text=Hi,%0A%0AI%20want%20to%20know%20more%20about%20your%20services.',
        external: true,
        primary: false,
      },
    ],
    accentColor: 'accent',
  },
];

const contactInfo = [
  {
    icon: 'ri-phone-line',
    label: 'Phone / WhatsApp',
    value: '+91 9746765488',
    href: 'tel:+919746765488',
  },
  {
    icon: 'ri-global-line',
    label: 'Website',
    value: 'www.dgsmart.in',
    href: 'https://www.dgsmart.in',
    external: true,
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'info@dgsmart.in',
    href: 'mailto:info@dgsmart.in',
  },
];

export default function OfficesSection() {
  const { ref, visible } = useReveal();

  return (
    <section id="offices" className="relative py-20 md:py-28 bg-background-100 overflow-hidden">
      {/* Subtle decorative blobs */}
      <div className="absolute top-0 left-0 w-[450px] h-[450px] rounded-full bg-primary-500/3 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-accent-500/3 blur-3xl pointer-events-none"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-18">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold uppercase tracking-wider mb-5">
            Our Locations
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
            Our Offices
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl mx-auto">
            Visit us or connect with our team from our Bengaluru Head Office or Kerala Branch Office.
          </p>
        </div>

        {/* Office Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto mb-16 md:mb-20">
          {offices.map(office => (
            <div
              key={office.id}
              className="group relative bg-background-50/80 backdrop-blur-xl border border-background-200/70 rounded-[20px] p-8 md:p-10 flex flex-col transition-all duration-500 hover:border-background-300/70 hover:-translate-y-1.5 hover:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.06)]"
            >
              {/* Subtle glass shimmer on hover */}
              <div className="absolute inset-0 rounded-[20px] bg-gradient-to-br from-primary-500/2 via-transparent to-accent-500/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

              <div className="relative z-10 flex flex-col flex-1">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-colors duration-300 ${
                  office.accentColor === 'primary'
                    ? 'bg-primary-50 group-hover:bg-primary-100'
                    : 'bg-accent-50 group-hover:bg-accent-100'
                }`}>
                  <i className={`${office.icon} text-2xl ${
                    office.accentColor === 'primary' ? 'text-primary-500' : 'text-accent-500'
                  }`}></i>
                </div>

                {/* Badge */}
                <div className="mb-3">
                  <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase bg-gradient-to-r ${
                    office.accentColor === 'primary'
                      ? 'from-primary-500 to-primary-400 text-white shadow-[0_2px_8px_rgba(30,94,255,0.2)]'
                      : 'from-accent-500 to-accent-400 text-white shadow-[0_2px_8px_rgba(0,180,180,0.2)]'
                  }`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80"></span>
                    {office.badge}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-5">
                  {office.title}
                </h3>

                {/* Address */}
                <div className="flex-1 mb-8">
                  <address className="not-italic text-sm md:text-base text-foreground-600 leading-relaxed space-y-0.5">
                    {office.address.map((line, i) => (
                      <span key={i} className="block">{line}</span>
                    ))}
                  </address>
                </div>

                {/* Divider */}
                <div className="h-px bg-background-200/70 mb-6"></div>

                {/* Buttons */}
                <div className="space-y-3">
                  {office.buttons.map(btn => (
                    <a
                      key={btn.label}
                      href={btn.href}
                      target={btn.external ? '_blank' : undefined}
                      rel={btn.external ? 'noopener noreferrer' : undefined}
                      className={`group/btn w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        btn.primary
                          ? office.accentColor === 'primary'
                            ? 'bg-primary-500 text-white hover:bg-primary-600 hover:scale-[1.01] active:scale-[0.99] shadow-[0_4px_14px_rgba(30,94,255,0.2)] hover:shadow-[0_6px_20px_rgba(30,94,255,0.3)]'
                            : 'bg-accent-500 text-white hover:bg-accent-600 hover:scale-[1.01] active:scale-[0.99] shadow-[0_4px_14px_rgba(0,180,180,0.18)] hover:shadow-[0_6px_20px_rgba(0,180,180,0.25)]'
                          : 'bg-background-100 text-foreground-700 hover:text-foreground-900 hover:bg-background-200/80'
                      }`}
                    >
                      <i className={`${btn.icon} text-base ${
                        btn.primary ? '' : 'text-foreground-500 group-hover/btn:text-foreground-700'
                      }`}></i>
                      {btn.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Information Strip */}
        <div className="max-w-3xl mx-auto">
          <div className="relative bg-background-50/80 backdrop-blur-sm border border-background-200/70 rounded-[20px] px-6 py-6 md:px-10 md:py-8 overflow-hidden">
            {/* Subtle glow */}
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-80 h-20 rounded-full bg-primary-500/4 blur-3xl pointer-events-none"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-center md:divide-x divide-background-200/70 gap-4 md:gap-0">
              {contactInfo.map((info, i) => (
                <a
                  key={info.label}
                  href={info.href}
                  target={info.external ? '_blank' : undefined}
                  rel={info.external ? 'noopener noreferrer' : undefined}
                  className={`group flex items-center gap-3 px-6 py-3 rounded-2xl hover:bg-background-100 transition-all duration-300 cursor-pointer ${
                    i === 0 ? '' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors duration-300">
                    <i className={`${info.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-0.5">
                      {info.label}
                    </p>
                    <p className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors duration-300 truncate">
                      {info.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}